import seedgreenAPI from "@/utils/seedgreen-api";
import { sendToast } from "@/utils/sendToast";
import { useLotSearchStore } from "@/views/LotSearch/LotSearchStore";
import { alertController, loadingController, useIonRouter } from "@ionic/vue";
import { useQueryClient } from "@tanstack/vue-query";
import { AxiosError } from "axios";
import { useI18n } from "vue-i18n";
import { captureException } from "@sentry/vue";
import { usePostHog } from "@/composables/usePosthog";

export default function useLogout() {
	const queryClient = useQueryClient();
	const router = useIonRouter();
	const { safeReset } = usePostHog();
	const { t } = useI18n();
	const lotSearchStore = useLotSearchStore();

	/**
	 * Handles logging out the user, including clearing queries and cache. Does NOT
	 * show any UI.
	 */
	async function _logout() {
		if (!navigator.onLine) {
			await sendToast("No network connection detected", 3000, "error");
			throw new Error("You must be online to log out");
		}

		try {
			await seedgreenAPI.post("v2/auth/logout");
		} catch (error) {
			// Ignore 401 errors - user is already logged out
			if ((error as AxiosError).status != 401) {
				console.error("Error logging out:", error);
				captureException(error);
			}
		}

		lotSearchStore.$reset(); // Use captured reference instead of calling composable
		await queryClient.invalidateQueries({ queryKey: ["verify-token"] });
		await queryClient.resetQueries();
		safeReset(); // Reset posthog user properties
	}

	/**
	 * Immediatly logs out the user, and shows a loading spinner
	 * @param redirectToHome - Whether to redirect to home page after logout (default: true)
	 */
	async function logout(redirectToHome = true) {
		const loading = await loadingController.create({
			message: "Logging out...",
		});
		await loading.present();
		try {
			await _logout();
			if (redirectToHome) {
				router.replace("/");
			}
		} finally {
			await loading.dismiss();
		}
	}

	/**
	 * Asks the user for confirmation before logging out.
	 */
	async function logoutWithConfirmation() {
		const alert = await alertController.create({
			header: t("common.logout"),
			message: t("common.logout-confirm"),
			buttons: [
				{
					text: t("common.cancel"),
					role: "cancel",
				},
				{
					text: t("common.logout"),
					handler: async () => {
						await alert.dismiss();
						await logout();
					},
				},
			],
		});
		await alert.present();
	}

	return { logoutWithConfirmation, logout };
}
